import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import { BASE_PATH_1_QUERY, BASE_PATH_2_QUERY } from '@glass/common/modules/pages/queryParams';
import { EDITABLE_RESUME_SECTION_ID } from '@glass/web/modules/content/pages/contentIds';
import useInView from '@glass/web/modules/loading/useInView';
import { EDITABLE_FORM_NAME } from '@glass/web/modules/reduxForm/formNames';
import { RESUME_V2_FIELD, TEMPLATE_FIELD_V2 } from '@glass/web/modules/resumes/fieldKeys';
import useBuilderBasePath from '@glass/web/modules/resumes/router/resumeBuilderBasePath/useBuilderBasePath';
import { CONTACT_SECTION } from '@glass/web/modules/resumes/router/sections';

import getBuilderNextSectionParams from '@glass/shared/components/Link/ResumeBuilderLink/getBuilderNextSectionParams';
import BuilderForm from '@glass/shared/components/ResumeBuilder/BuilderForm';
import EditableResume from '@glass/shared/components/Section/EditableResumeSection/EditableResume';
import Section from '@glass/shared/components/Section/Section';
import mergeRefs from '@glass/shared/helpers/utils/mergeRefs';
import useContentSeed from '@glass/shared/modules/content/useContentSeed';
import useContactInitialValues from '@glass/shared/modules/resumes/hooks/useContactInitialValues';
import useTemplate from '@glass/shared/modules/resumes/hooks/useTemplate';
import withTrackingContext from '@glass/shared/modules/tracking/withTrackingContext';

const trackProps = { context: 'editable-resume-section' };
const IN_VIEW_ROOT_MARGIN = '200px 0px';

const EditableResumeSection = React.forwardRef(
  (
    {
      seedOverride,
      statusTitle,
      title,
      fallbackId,
      subtitle,
      disableCta,
      isAlternatingColor,
      type,
    },
    refProp,
  ) => {
    const { ref: refInView, inView } = useInView({
      triggerOnce: true,
      rootMargin: IN_VIEW_ROOT_MARGIN,
    });
    const isDocumentReadyPriorityLoadedIdleInView = inView;
    const contactInitialValues = useContactInitialValues({ isMinimal: true });

    const seed = useContentSeed({ fallbackId, seedOverride });

    const templateOptions = useMemo(
      () => ({
        seedOverride: seed,
        skip: !isDocumentReadyPriorityLoadedIdleInView,
        ssr: false,
      }),
      [seed, isDocumentReadyPriorityLoadedIdleInView],
    );

    const { template: templateSettings, loading: templateLoading } = useTemplate(templateOptions);

    const defaultBuilderBasePath = useBuilderBasePath();
    const nextStepParams = useMemo(
      () =>
        getBuilderNextSectionParams({
          [BASE_PATH_1_QUERY]: defaultBuilderBasePath,
          [BASE_PATH_2_QUERY]: CONTACT_SECTION,
        }),
      [defaultBuilderBasePath],
    );

    const initialValues = useMemo(() => {
      // Return base contact values during template loading to prevent unnecessary form recreation
      if (templateLoading || !templateSettings) {
        return contactInitialValues;
      }

      return {
        ...contactInitialValues,
        [RESUME_V2_FIELD]: {
          ...contactInitialValues[RESUME_V2_FIELD],
          [TEMPLATE_FIELD_V2]: templateSettings,
        },
      };
    }, [contactInitialValues, templateSettings, templateLoading]);

    const ref = useMemo(() => mergeRefs(refProp, refInView), [refProp, refInView]);
    const show =
      !isDocumentReadyPriorityLoadedIdleInView || templateLoading || Boolean(templateSettings);

    if (!show) {
      return null;
    }

    return (
      <Section
        ref={ref}
        disableCta={disableCta}
        isAlternatingColor={isAlternatingColor}
        subtitle={subtitle}
        title={title}
        type={type}
      >
        <BuilderForm
          destroyOnUnmount
          isExternalBuilder
          keepDirtyOnReinitialize
          currentSection={CONTACT_SECTION}
          form={EDITABLE_FORM_NAME}
          initialValues={initialValues}
          nextStepParams={nextStepParams}
          trackProps={trackProps}
        >
          <EditableResume
            loading={templateLoading}
            seedOverride={seedOverride}
            skip={!isDocumentReadyPriorityLoadedIdleInView}
            statusTitle={statusTitle}
          />
        </BuilderForm>
      </Section>
    );
  },
);

EditableResumeSection.defaultProps = {
  disableCta: true,
  title: `Use this $(resumeWords.abbreviated) to start <b>your&nbsp;own</b>`,
  subtitle: `<b>Start</b> with this $(contentWords.singular) to $(verbWords.singular) the perfect $(resumeWords.singular), we&apos;ll help you along the way`,
};

EditableResumeSection.propTypes = {
  disableCta: PropTypes.bool,
  fallbackId: PropTypes.string,
  isAlternatingColor: PropTypes.bool,
  seedOverride: PropTypes.string,
  statusTitle: PropTypes.string,
  subtitle: PropTypes.string,
  title: PropTypes.string,
  type: PropTypes.string,
};

EditableResumeSection.displayName = 'EditableResumeSection';

export default React.memo(withTrackingContext(EditableResumeSection, EDITABLE_RESUME_SECTION_ID));
